import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Show cached user instantly — validate in background
  const [user, setUser] = useState(() => {
    try {
      const s = localStorage.getItem('user');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem('user'));
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('user'));

  const checkAuth = useCallback(async () => {
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        // Cookie is sent automatically — verify it's still valid
        const response = await authAPI.getMe();
        const freshUser = response.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { user: userData } = response.data;
    // Cookies are set by the server (httpOnly) — only store non-sensitive user info
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const register = async (email, password, name, termsAccepted = false) => {
    const response = await authAPI.register(email, password, name, termsAccepted);
    const { user: userData } = response.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const googleLogin = async (sessionId) => {
    const response = await authAPI.googleCallback(sessionId);
    const data = response.data;
    const userData = data.user;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return data;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    setUser,
    loading,
    isAuthenticated,
    login,
    register,
    googleLogin,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
